import { makeConsultation } from '@/api/partnerShopDetail';
import { useState } from 'react';
import { DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
// import 'antd/dist/reset.css';

interface ConsultationInquiryFormProps {
  slug?: string;
  onSuccess: () => void;
}

type FieldProps = {
  htmlFor: string;
  label: string;
  error?: string;
  children: React.ReactNode;
};

function FormField({ htmlFor, label, error, children }: FieldProps) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-[13px] font-medium text-[#121B35] mb-1.5">
        {label}
      </label>
      {children}
      {error && <p className="text-[11px] text-red-400 mt-1">{error}</p>}
    </div>
  );
}

export default function ConsultationInquiryForm({ slug, onSuccess }: ConsultationInquiryFormProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [visitDate, setVisitDate] = useState<Dayjs | null>(null);
  const [etc, setEtc] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhone(e.target.value));
    if (errors.phone) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.phone;
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) {
      newErrors.name = '이름을 입력해주세요.';
    }
    if (!phone.trim()) {
      newErrors.phone = '전화번호를 입력해주세요.';
    } else if (phone.replace(/\D/g, '').length < 10) {
      newErrors.phone = '올바른 전화번호를 입력해주세요.';
    }
    if (!visitDate) {
      newErrors.visitDate = '방문희망일을 선택해주세요.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);

    const payload = {
      name: name.trim(),
      phone: phone.trim(),
      visitDate: visitDate ? visitDate.format('YYYY-MM-DD') : '',
      etc: etc.trim(),
      shopSlug: slug || '',
    };

    // TODO: 실제 API 호출로 교체
    // console.log('[ConsultationInquiry] Submit payload:', payload);

    const result = await makeConsultation(payload)
    if (result.status !== 200) {
      alert(result.message)
      setSubmitting(false)
      return
    }

    setTimeout(() => {
      setSubmitting(false);
      onSuccess();
    }, 2500);
  };

  const clearFieldError = (field: string) => {
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
          e.preventDefault();
        }
      }}
      className="flex flex-col gap-4"
    >
      <FormField htmlFor="consult-name" label="이름" error={errors.name}>
        <input
          id="consult-name"
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            clearFieldError('name');
          }}
          placeholder="이름을 입력해주세요"
          className={`w-full px-3.5 py-2.5 rounded-xl border text-[14px] text-[#121B35] placeholder-[#B0B8C1] bg-white outline-none transition-colors ${
            errors.name ? 'border-red-400' : 'border-[#E8E2DA] focus:border-[#B9965B] focus:ring-2 focus:ring-[#B9965B]/20'
          }`}
          autoComplete="name"
        />
      </FormField>

      <FormField htmlFor="consult-phone" label="전화번호" error={errors.phone}>
        <input
          id="consult-phone"
          type="tel"
          inputMode="tel"
          value={phone}
          onChange={handlePhoneChange}
          placeholder="010-0000-0000"
          className={`w-full px-3.5 py-2.5 rounded-xl border text-[14px] text-[#121B35] placeholder-[#B0B8C1] bg-white outline-none transition-colors ${
            errors.phone ? 'border-red-400' : 'border-[#E8E2DA] focus:border-[#B9965B] focus:ring-2 focus:ring-[#B9965B]/20'
          }`}
          autoComplete="tel"
        />
      </FormField>

      <FormField htmlFor="consult-date" label="방문희망일" error={errors.visitDate}>
        <DatePicker
          id="consult-date"
          value={visitDate}
          onChange={(date) => {
            setVisitDate(date);
            clearFieldError('visitDate');
          }}
          format="YYYY-MM-DD"
          placeholder="YYYY-MM-DD"
          allowClear={false}
          style={{
            width: '100%',
            borderRadius: '1rem',
            height: '2.75rem',
            fontSize: '14px',
            color: '#121B35',
            backgroundColor: '#fff',
            borderColor: errors.visitDate ? '#f87171' : '#E8E2DA',
          }}
          className={`w-full px-3.5 py-2.5 rounded-xl border text-[14px] text-[#121B35] placeholder-[#B0B8C1] bg-white outline-none transition-colors ${
            errors.visitDate ? 'border-red-400' : 'border-[#E8E2DA] focus:border-[#B9965B] focus:ring-2 focus:ring-[#B9965B]/20'
          }`}
          popupStyle={{ zIndex: 1000 }}
        />
      </FormField>

      <FormField htmlFor="consult-etc" label="요청사항">
        <textarea
          id="consult-etc"
          value={etc}
          onChange={(e) => setEtc(e.target.value)}
          placeholder="요청사항을 입력해주세요"
          rows={4}
          className="w-full px-3.5 py-3 rounded-xl border text-[14px] text-[#121B35] placeholder-[#B0B8C1] bg-white outline-none transition-colors border-[#E8E2DA] focus:border-[#B9965B] focus:ring-2 focus:ring-[#B9965B]/20"
        />
      </FormField>
      
      {/* Submit */}
      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-[#121B35] text-white font-semibold rounded-xl py-3 text-[14px] whitespace-nowrap active:opacity-90 transition-opacity disabled:opacity-60 mt-1"
      >
        {submitting ? '접수 중...' : '상담 문의하기'}
      </button>
    </form>
  );
}